import Controller, { OnChange, OnClick, OnMount, OnReadSuccess } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService, MessageService,  } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../constants/endpoints';
import BaseController from '../../BaseController';
import InformationBarUtils from '../../commons/header/information-bar-header-utils';
import { Inject } from '@odinjs/odin';
import './medical-prescriptions-list-controller.css'
import { FragmentNames, MedicalPrescriptionsListAliases } from '../../../codegen';
import { BarcodeAPI, BarcodeData, ButtonAPI, FieldAPI, FormAPI, ToastService } from '@philips-internal/oswald-ui-components';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { Value } from '@philips-internal/oswald-ui-core';
import { ClickEvent, MountEvent, ReadEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { ApplicationRoute } from '../../../constants/routes';
import { isFunction } from '@philips-internal/oswald-ui-utils';
import { Placement, ToastLevel } from '@philips-internal/oswald-ui-constants';
import { FoundationUtils } from '../../../utils/foundation-utils';
import BarcodeService from '../../../services/barcode-service';

interface TasyParameters {
  cdFunction: string;
  name: string;
  parameters: Record<string, Value>;
}

interface ResponseValidator {
  body: {
    iePrescrValid: string;
  }
}

interface ClickCard {
  data: string,
  ieRn: string,
  nmMdPrescr: string,
  nrPrescription: number
}

@Controller({ component: MedicalPrescriptionsListAliases.LAYOUT_CONFIRMATION_PRESCRIPTIONS_LIST, fragment: FragmentNames.MEDICAL_PRESCRIPTIONS_LIST })
class MedicalPrescriptionsListController extends BaseController {

  @Inject(MedicalPrescriptionsListAliases.FIELD_INPUT_CONFIRMATION_PRESCRIPTIONS_LIST)
  private fieldInputBarcodeConfirmation!: FieldAPI;

  @Inject(MedicalPrescriptionsListAliases.BUTTON_CONFIRMATION_PRESCRIPTIONS_LIST)
  private buttonBarcodeConfirmation!: ButtonAPI;

  handleParamTasyTwsParam(
    resultTwsParameters: TasyParameters[] | { parameters: { [x: string]: string; }; }[],
    resultTasyParameters: TasyParameters[] | { parameters: { [x: string]: string; }; }[],
    paramType: string
  ): string | null {
    const paramsValue = String(resultTwsParameters[0].parameters[paramType]
      ? resultTwsParameters[0].parameters[paramType]
      : resultTasyParameters[0].parameters[paramType]);

    if (paramsValue === 'undefined') {
      return null;
    }

    return paramsValue;
  }

  async confirmationBarcodePresciption(): Promise<void> {
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const valueInput = this.context.get('inputBarcodeConfirmation');
    const existPrescription = await HttpService.get(Endpoint.EXAM_COLLECT_MEDICAL_EXIST_PRESCRIPTION, {
      encounterId, prescriptionId: valueInput
    });

    const { body } = existPrescription as ResponseValidator;

    const message = MessageService.translate('text.prescription-does-not-belong-to-the-service').replace('#@NR_ATENDIMENTO#@', `${encounterId}`);

    const validatorObj = {
      message,
      name: '',
      valid: false
    }

    if (body && body.iePrescrValid === 'N') {
      const validatorName = 'prescription-validator';
      this.fieldInputBarcodeConfirmation.addLocalValidator(validatorName, async () => ([validatorObj]));
      this.fieldInputBarcodeConfirmation.validate();
      this.fieldInputBarcodeConfirmation.removeLocalValidator(validatorName);
      this.buttonBarcodeConfirmation.setEnabled(false);
    }
  }

  redirectPrescription(value: ClickCard): void {
    const prescriptionId = value.nrPrescription;

    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();

    applicationContext.redirect(
      ApplicationRoute.EXAM_COLLECT_PENDING_LIST,
      { encounterId, patientId, prescriptionId }
    );
  }

  errorMessage(): void{
    const { encounterId } = this.historyService.getCurrentRouteParams();
    const errorMenssage = MessageService.translate('message.prescription-does-not-belong-to-the-service').replace('#@NR_ATENDIMENTO#@', `${encounterId}`);
    const titleMessage = MessageService.translate('text.message-alert-attention');
    ToastService.close('custom-toast');
    ToastService.open({
      dismissible: true,
      id: 'custom-toast',
      level: ToastLevel.ERROR,
      message: errorMenssage,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      title: titleMessage,
    });
  }

  returnToFilterScreenAndBarcode(): void {
    applicationContext.clearNavigationStack();
    applicationContext.pushNavigationItem(() => {
      this.historyService.push(
        `${ApplicationRoute.EXAM_COLLECTION}${ApplicationRoute.SEARCH_PATIENT}`
      );
    });
  }

  validateBarcode(event: ReadEvent<BarcodeData>): void{
    const inputValue = event.item.value;
    const braceletNumber = Number(inputValue);

    if (!braceletNumber) {
      this.errorMessage();
      return;
    }

    const {preventDefault} = event;
    if (isFunction(preventDefault)) {
      preventDefault();
    }
  }

  @OnMount(MedicalPrescriptionsListAliases.BARCODE_COMPONENT)
  handleMountBarcode(event: MountEvent): void {
    const barcodeApi = event.target as BarcodeAPI
    barcodeApi.setDefaultReadType(BarcodeService.getDefaultMethodOfReadingBarcode())
  }

  @OnReadSuccess(MedicalPrescriptionsListAliases.BARCODE_COMPONENT)
  onReadSucesstBarCode(event: ReadEvent<BarcodeData>): void {
    const barcode = event.target as BarcodeAPI;
    this.validateBarcode(event);
    const { encounterId } = this.historyService.getCurrentRouteParams();

    const params = {
      encounterNumber: encounterId,
      limit: 1,
      page: 0,
      prescriptionNumber: event.item.value.trim(),
    }
    barcode.close();
    barcode.setEnabled(false);

    HttpService.get(Endpoint.EXAM_COLLECT_LIST_PENDING, params).then((resp) => {
      const body = resp.body as ClickCard[];
      barcode.setEnabled(true);
      if (body.length === 0) {
        barcode.setOpen(true);
        return this.errorMessage();
      }
      const prescriptionId = event.item.value;
      const { patients, encounters } = this.historyService.getCurrentRouteParams();
      return applicationContext.redirect(
        ApplicationRoute.EXAM_COLLECT_PENDING_LIST,
        {
          encounters,
          patients,
          prescriptionId,
        }
      );
    }).catch(() => {
      barcode.setEnabled(true);
      barcode.setOpen(true);
    })
  }

  @OnMount(MedicalPrescriptionsListAliases.LAYOUT_MEDICAL_PRESCRIPTION_LIST)
  async onMount(): Promise<void> {
    InformationBarUtils.collapsedBar = true;
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    const EXAM_COLLECTION_MODULE = 'EXAM_COLLECT#9130';
    const EXAM_COLLECT_DEFAULT_VALUE = 'EXAM_COLLECT#9130';

    const paramLaboratory = FoundationUtils.findModuleParam(EXAM_COLLECTION_MODULE, 'USE_LABORATORY_SAMPLE_STANDARD#2') || FoundationUtils.findModuleParam(EXAM_COLLECT_DEFAULT_VALUE, 'USE_LABORATORY_SAMPLE_STANDARD#2');
    const statusExam = FoundationUtils.findModuleParam(EXAM_COLLECTION_MODULE, 'PENDING_EXAMS_STATUS#3') || FoundationUtils.findModuleParam(EXAM_COLLECT_DEFAULT_VALUE, 'PENDING_EXAMS_STATUS#3');
    const qtValid = FoundationUtils.findModuleParam(EXAM_COLLECTION_MODULE, 'HOURS_PRESCRIPTIONS_COLLECTION#4') || FoundationUtils.findModuleParam(EXAM_COLLECT_DEFAULT_VALUE, 'HOURS_PRESCRIPTIONS_COLLECTION#4');
    const qtHoursRecollect = FoundationUtils.findModuleParam(EXAM_COLLECTION_MODULE, 'HOURS_PRESCRIPTIONS_RECOLLECT#5') || FoundationUtils.findModuleParam(EXAM_COLLECT_DEFAULT_VALUE, 'HOURS_PRESCRIPTIONS_RECOLLECT#5');
    const dtPrevPrescriptions = FoundationUtils.findModuleParam(EXAM_COLLECTION_MODULE, 'DISPLAY_EXPECTED_DATE_PRESCRIPTIONS#6') || FoundationUtils.findModuleParam(EXAM_COLLECT_DEFAULT_VALUE, 'DISPLAY_EXPECTED_DATE_PRESCRIPTIONS#6');
    const groupExam = FoundationUtils.findModuleParam(EXAM_COLLECTION_MODULE, 'EXAMS_GROUPS_RELEASED_PREVIEW#7')

    this.context.set({
      dtprevprescriptionsId: dtPrevPrescriptions,
      encounterId,
      groupexamId: groupExam ? groupExam : null,
      paramlaboratoryId: paramLaboratory ? paramLaboratory : null,
      patientId,
      qthoursId: qtValid ? qtValid : 0,
      qthoursrecollectId: qtHoursRecollect ? qtHoursRecollect : 0,
      qtvalidId: qtValid ? qtValid : 0,
      qtvalidrecollectId: qtHoursRecollect ? qtHoursRecollect : 0,
      statusexamId: statusExam ? statusExam : null,
    });

    this.returnToFilterScreenAndBarcode();
  }

  @OnChange(MedicalPrescriptionsListAliases.INPUT_CONFIRMATION_PRESCRIPTIONS_LIST)
  onChangeInputBarcodeConfirmation(event: FormAPI): void {
    const apiData = event.getData();
    this.context.set({
      inputBarcodeConfirmation: apiData,
    });

    if (apiData) {
      return this.buttonBarcodeConfirmation.setEnabled(true);
    }

    return this.buttonBarcodeConfirmation.setEnabled(false);
  }

  @OnClick(MedicalPrescriptionsListAliases.BUTTON_CONFIRMATION_PRESCRIPTIONS_LIST)
  onClickButtonBarcodeConfirmation(): void {
    this.confirmationBarcodePresciption();
  }

  @OnClick(MedicalPrescriptionsListAliases.DECK_MEDICAL_PRESCRIPTIONS_LIST_CARD)
  onClickMedicalPrescriptionCard(evt: ClickEvent): void {
    const value = evt.value as ClickCard;
    this.redirectPrescription(value);
  }

}

export default MedicalPrescriptionsListController;
